import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text/index';
import { noop } from 'ui-components/default-props/noop';

import { messages } from './messages';
import { getSanitizedName } from '../../../components/data-driven-logo/name-utils';
import { HelpArticleLink } from '../../../components/help-article-link';
import { SimpleModal } from '../../../components/simple-modal';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { useModal } from '../../../hooks/use-modal';
import { getShipmentPickupsPath } from '../../../routes/constants';
import Global from '../../../store/global-selectors';
import * as UserActions from '../../../store/user/user.actions';
import { AmazonPickup } from '../amazon-pickup';

/**
 * SchedulePickupPrompt component - prompts user to schedule a pickup
 * @param {object} props
 * @param {() => void} [props.onClose] - Callback when modal is closed
 * @param {string} [props.sellerLabelProviderId] - ID of the seller label provider
 * @returns {React.ReactElement} The SchedulePickupPrompt component
 */
export const SchedulePickupPrompt = ({ onClose = noop, sellerLabelProviderId = '' }) => {
  const dispatch = useDispatch();
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const labelProviders = useSelector(Global.selectReferenceDatagetProviders);

  const sellerLabelProvider = sellerLabelProviders.find((provider) => provider.sellerLabelProviderId === sellerLabelProviderId);
  const labelProvider = labelProviders.find((provider) => provider.labelProviderId === sellerLabelProvider.labelProviderId);
  const labelProviderName = getSanitizedName(labelProvider.name);

  const { isOpen: isPickupModalOpen, open: openPickupModal, close: closePickupModal } = useModal();

  const handleSchedulePickup = () => {
    openPickupModal();
  };

  const closeWithEffects = () => {
    onClose();
    dispatch(
      UserActions.saveUserSetting({
        settingName: UserSettings.isAmazonShippingFirstLabelCreated.settingName,
        settingValue: 'true',
        excludeV3prefix: true,
      }),
    );
  };

  const closeItAll = () => {
    closeWithEffects();
    closePickupModal();
  };

  return (
    <>
      <SimpleModal
        actionMessage={messages.schedulePickupButton}
        cancelMessage={messages.doLaterButton}
        headerMessage={labelProviderName}
        headerTextType={TextType.H3}
        onAction={handleSchedulePickup}
        onCancel={closeWithEffects}
        onClose={closeWithEffects}
        size='small'
      >
        <FlexColumn gap='gap-16'>
          <Text textType={TextType.H3} value={<FormattedMessage {...messages.needToSchedulePickupTitle} />} />
          <Text
            textType={TextType.Paragraph}
            value={<FormattedMessage {...messages.requiresScheduledPickupMessage} values={{ labelProviderName }} />}
          />
          <Text
            formattedMessageValues={{
              link: (chunks) => <Link to={getShipmentPickupsPath()}>{chunks}</Link>,
            }}
            textType={TextType.Paragraph}
            value={messages.scheduleNowOrLaterMessage}
          />
          <Text
            textType={TextType.Paragraph}
            value={
              <HelpArticleLink url='https://help.shipstation.com/hc/en-us/articles/40691569416859'>
                <FormattedMessage {...messages.learnMoreMessage} values={{ labelProviderName }} />
              </HelpArticleLink>
            }
          />
        </FlexColumn>
      </SimpleModal>
      {isPickupModalOpen ? <AmazonPickup onClose={closeItAll} sellerLabelProviderId={sellerLabelProviderId} /> : null}
    </>
  );
};
